import { useState, useRef, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Type, Image, Wand2, Loader2, Trash2, Square, X, Plus
} from "lucide-react";

// Default fonts
const DEFAULT_FONTS = [
  { name: "Arial", family: "Arial, sans-serif" },
  { name: "Helvetica", family: "Helvetica, sans-serif" },
  { name: "Georgia", family: "Georgia, serif" },
  { name: "Verdana", family: "Verdana, sans-serif" },
];

export default function CreativeEditor({ 
  creative, 
  open, 
  onOpenChange, 
  token, 
  companyAssets = [],
  brandColors = [],
  brandFonts = [],
  onUpdate 
}) {
  const [overlayElements, setOverlayElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [regeneratePrompt, setRegeneratePrompt] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Local state for image URL (allows immediate preview after regeneration)
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [pendingRegeneration, setPendingRegeneration] = useState(null); // Stores new image before confirmation
  const [originalImageUrl, setOriginalImageUrl] = useState(null); // For cancel functionality
  
  // Drag state
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Resize state
  const [resizing, setResizing] = useState(null);
  
  // Track if we just finished an interaction (to prevent deselection)
  const justFinishedInteraction = useRef(false);
  
  const canvasRef = useRef(null);
  
  // Form states for adding new elements
  const [newText, setNewText] = useState("New Text");
  const [newTextColor, setNewTextColor] = useState("#FFFFFF");
  const [newTextFont, setNewTextFont] = useState("Arial, sans-serif");
  
  const [newImageAsset, setNewImageAsset] = useState("");
  
  const [newButtonText, setNewButtonText] = useState("Click Here");
  const [newButtonShape, setNewButtonShape] = useState("rounded");
  const [newButtonBg, setNewButtonBg] = useState("#3B82F6");
  const [newButtonTextColor, setNewButtonTextColor] = useState("#FFFFFF");
  
  const availableFonts = [...DEFAULT_FONTS, ...brandFonts];

  // Initialize local image URL from creative prop
  useEffect(() => {
    if (creative?.file_url) {
      setCurrentImageUrl(creative.file_url);
      setOriginalImageUrl(creative.file_url);
      setPendingRegeneration(null);
    }
  }, [creative?.file_url]);

  useEffect(() => {
    if (creative?.ai_prompt) {
      setRegeneratePrompt(creative.ai_prompt);
    }
    if (creative?.id && open) {
      loadOverlays();
    }
  }, [creative, open]);

  const loadOverlays = async () => {
    try {
      const response = await axios.get(`${API}/creatives/${creative.id}/overlay`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverlayElements(response.data.elements || []);
    } catch (error) {
      console.error("Failed to load overlays", error);
    }
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/creatives/${creative.id}/composite`, 
        { elements: overlayElements },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Creative saved with overlays!");
      onUpdate && onUpdate();
      onOpenChange(false);
    } catch (error) {
      try {
        await axios.put(`${API}/creatives/${creative.id}/overlay`, overlayElements, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Overlays saved (preview only)");
      } catch (e) {
        toast.error("Failed to save changes");
      }
    } finally {
      setSaving(false);
    }
  };

  // Add handlers
  const addTextElement = () => {
    if (!newText.trim()) return;
    const newElement = {
      id: `text-${Date.now()}`,
      type: "text",
      x: 50 - 15,
      y: 50 - 7.5,
      width: 30,
      height: 15,
      text: newText,
      font_size: 24,
      font_family: newTextFont,
      font_color: newTextColor,
      font_weight: "bold",
      opacity: 1,
      z_index: overlayElements.length + 1
    };
    setOverlayElements([...overlayElements, newElement]);
    setNewText("New Text");
  };

  const addImageElement = () => {
    if (!newImageAsset) return;
    const asset = companyAssets.find(a => a.id === newImageAsset);
    if (!asset) return;
    
    const newElement = {
      id: `image-${Date.now()}`,
      type: "logo",
      x: 50 - 10,
      y: 50 - 10,
      width: 20,
      height: 20,
      asset_id: asset.id,
      image_url: asset.file_url,
      opacity: 1,
      z_index: overlayElements.length + 1
    };
    setOverlayElements([...overlayElements, newElement]);
    setNewImageAsset("");
  };

  const addButtonElement = () => {
    if (!newButtonText.trim()) return;
    const borderRadius = newButtonShape === 'pill' ? 50 : newButtonShape === 'rounded' ? 8 : 0;
    const newElement = {
      id: `button-${Date.now()}`,
      type: "button",
      x: 50 - 12.5,
      y: 70,
      width: 25,
      height: 12,
      button_text: newButtonText,
      button_color: newButtonBg,
      button_text_color: newButtonTextColor,
      border_radius: borderRadius,
      opacity: 1,
      z_index: overlayElements.length + 1
    };
    setOverlayElements([...overlayElements, newElement]);
    setNewButtonText("Click Here");
  };

  const deleteElement = (id) => {
    setOverlayElements(overlayElements.filter(el => el.id !== id));
    if (selectedElement === id) setSelectedElement(null);
  };

  const handleRegenerate = async () => {
    if (!regeneratePrompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }
    
    setRegenerating(true);
    try {
      const response = await axios.post(
        `${API}/creatives/${creative.id}/regenerate`,
        null,
        {
          params: { new_prompt: regeneratePrompt },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Show new image immediately in preview
      setCurrentImageUrl(response.data.file_url);
      setPendingRegeneration(response.data);
      toast.success("New image generated! Click 'Keep New Image' to save or 'Revert' to cancel.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Regeneration failed");
    } finally {
      setRegenerating(false);
    }
  };

  const handleKeepRegeneration = () => {
    if (pendingRegeneration) {
      onUpdate && onUpdate(pendingRegeneration);
      setOriginalImageUrl(pendingRegeneration.file_url);
      setPendingRegeneration(null);
      toast.success("New image saved!");
    }
  };

  const handleRevertRegeneration = () => {
    if (originalImageUrl) {
      setCurrentImageUrl(originalImageUrl);
      setPendingRegeneration(null);
      toast.info("Reverted to original image");
    }
  };

  // Drag handlers
  const handleMouseDown = (e, elementId) => {
    if (resizing) return; // Don't drag while resizing
    e.stopPropagation();
    e.preventDefault();
    
    const rect = canvasRef.current.getBoundingClientRect();
    const element = overlayElements.find(el => el.id === elementId);
    if (!element) return;
    
    const mouseX = ((e.clientX - rect.left) / rect.width) * 100;
    const mouseY = ((e.clientY - rect.top) / rect.height) * 100;
    
    setDragOffset({
      x: mouseX - element.x,
      y: mouseY - element.y
    });
    setDragging(elementId);
    setSelectedElement(elementId);
  };

  // Resize handlers
  const handleResizeStart = (e, elementId, corner) => {
    e.stopPropagation();
    e.preventDefault();
    
    const element = overlayElements.find(el => el.id === elementId);
    if (!element) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * 100;
    const mouseY = ((e.clientY - rect.top) / rect.height) * 100;
    
    setResizing({
      id: elementId,
      corner,
      startMouseX: mouseX,
      startMouseY: mouseY,
      startX: element.x,
      startY: element.y,
      startWidth: element.width,
      startHeight: element.height,
      aspectRatio: element.width / element.height
    });
    setSelectedElement(elementId);
  };

  const handleMouseMove = useCallback((e) => {
    if (!canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * 100;
    const mouseY = ((e.clientY - rect.top) / rect.height) * 100;
    
    // Handle dragging
    if (dragging && !resizing) {
      const newX = mouseX - dragOffset.x;
      const newY = mouseY - dragOffset.y;
      
      const element = overlayElements.find(el => el.id === dragging);
      if (!element) return;
      
      const clampedX = Math.max(0, Math.min(100 - element.width, newX));
      const clampedY = Math.max(0, Math.min(100 - element.height, newY));
      
      setOverlayElements(prev => prev.map(el => 
        el.id === dragging ? { ...el, x: clampedX, y: clampedY } : el
      ));
    }
    
    // Handle resizing
    if (resizing) {
      const { id, corner, startMouseX, startMouseY, startX, startY, startWidth, startHeight, aspectRatio } = resizing;
      
      const deltaX = mouseX - startMouseX;
      const deltaY = mouseY - startMouseY;
      
      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startX;
      let newY = startY;
      
      // Calculate new size based on corner being dragged
      // Use the larger delta to maintain aspect ratio
      if (corner === 'se') {
        // Bottom-right: increase size
        const delta = Math.max(deltaX, deltaY / aspectRatio);
        newWidth = Math.max(5, startWidth + delta);
        newHeight = newWidth / aspectRatio;
      } else if (corner === 'sw') {
        // Bottom-left: move x, increase height
        const delta = Math.max(-deltaX, deltaY / aspectRatio);
        newWidth = Math.max(5, startWidth + delta);
        newHeight = newWidth / aspectRatio;
        newX = startX + startWidth - newWidth;
      } else if (corner === 'ne') {
        // Top-right: move y, increase width
        const delta = Math.max(deltaX, -deltaY * aspectRatio);
        newWidth = Math.max(5, startWidth + delta);
        newHeight = newWidth / aspectRatio;
        newY = startY + startHeight - newHeight;
      } else if (corner === 'nw') {
        // Top-left: move both x and y
        const delta = Math.max(-deltaX, -deltaY * aspectRatio);
        newWidth = Math.max(5, startWidth + delta);
        newHeight = newWidth / aspectRatio;
        newX = startX + startWidth - newWidth;
        newY = startY + startHeight - newHeight;
      }
      
      // Clamp values
      newX = Math.max(0, Math.min(100 - newWidth, newX));
      newY = Math.max(0, Math.min(100 - newHeight, newY));
      newWidth = Math.min(100 - newX, newWidth);
      newHeight = Math.min(100 - newY, newHeight);
      
      setOverlayElements(prev => prev.map(el => 
        el.id === id ? { ...el, x: newX, y: newY, width: newWidth, height: newHeight } : el
      ));
    }
  }, [dragging, resizing, dragOffset, overlayElements]);

  const handleMouseUp = useCallback(() => {
    if (dragging || resizing) {
      justFinishedInteraction.current = true;
      // Reset the flag after a short delay
      setTimeout(() => {
        justFinishedInteraction.current = false;
      }, 100);
    }
    setDragging(null);
    setResizing(null);
  }, [dragging, resizing]);

  useEffect(() => {
    if (dragging || resizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, resizing, handleMouseMove, handleMouseUp]);

  if (!creative) return null;

  // Resize handle component
  const ResizeHandle = ({ elementId, corner, position }) => (
    <div
      className={`absolute w-3 h-3 bg-blue-500 border-2 border-white rounded-sm cursor-${
        corner === 'nw' || corner === 'se' ? 'nwse' : 'nesw'
      }-resize shadow-md z-50 hover:bg-blue-600 transition-colors`}
      style={position}
      onMouseDown={(e) => handleResizeStart(e, elementId, corner)}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit: {creative.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Image Preview with Overlays */}
          <div 
            ref={canvasRef}
            className="relative bg-gray-900 rounded-lg overflow-hidden select-none"
            style={{ 
              aspectRatio: creative.format === 'BANNER' ? '320/100' : '16/9',
            }}
            onClick={() => {
              // Don't deselect if we just finished dragging/resizing
              if (!justFinishedInteraction.current) {
                setSelectedElement(null);
              }
            }}
          >
            <img 
              src={currentImageUrl || creative.file_url} 
              alt={creative.name}
              className="w-full h-full object-cover pointer-events-none"
              draggable={false}
            />
            
            {/* Pending Regeneration Indicator */}
            {pendingRegeneration && (
              <div className="absolute top-2 left-2 right-2 bg-amber-500/90 text-white text-xs px-2 py-1 rounded flex items-center justify-between z-50">
                <span>New image preview - not saved yet</span>
                <div className="flex gap-1">
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleKeepRegeneration(); }}
                    className="bg-green-600 hover:bg-green-700 px-2 py-0.5 rounded text-xs"
                  >
                    Keep
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleRevertRegeneration(); }}
                    className="bg-red-600 hover:bg-red-700 px-2 py-0.5 rounded text-xs"
                  >
                    Revert
                  </button>
                </div>
              </div>
            )}
            
            {/* Overlay Elements */}
            {overlayElements.map(el => (
              <div
                key={el.id}
                className={`absolute group ${
                  selectedElement === el.id ? 'ring-2 ring-blue-500' : ''
                } ${dragging === el.id ? 'cursor-grabbing' : 'cursor-grab'}`}
                style={{
                  left: `${el.x}%`,
                  top: `${el.y}%`,
                  width: `${el.width}%`,
                  height: `${el.height}%`,
                  opacity: el.opacity,
                  zIndex: el.z_index
                }}
                onMouseDown={(e) => handleMouseDown(e, el.id)}
              >
                {/* Delete button */}
                <button
                  onClick={(e) => { e.stopPropagation(); deleteElement(el.id); }}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg"
                  title="Delete"
                >
                  <X className="h-3 w-3 text-white" />
                </button>
                
                {/* Resize handles - only show when selected */}
                {selectedElement === el.id && (
                  <>
                    <ResizeHandle elementId={el.id} corner="nw" position={{ top: -4, left: -4 }} />
                    <ResizeHandle elementId={el.id} corner="ne" position={{ top: -4, right: -4 }} />
                    <ResizeHandle elementId={el.id} corner="sw" position={{ bottom: -4, left: -4 }} />
                    <ResizeHandle elementId={el.id} corner="se" position={{ bottom: -4, right: -4 }} />
                  </>
                )}
                
                {el.type === "text" && (
                  <div 
                    className="w-full h-full flex items-center justify-center text-center px-1 overflow-hidden"
                    style={{
                      fontSize: `clamp(8px, ${el.height * 0.6}%, 200px)`,
                      fontFamily: el.font_family || 'Arial, sans-serif',
                      color: el.font_color,
                      fontWeight: el.font_weight,
                      textShadow: '1px 1px 3px rgba(0,0,0,0.9)'
                    }}
                  >
                    {el.text}
                  </div>
                )}
                {el.type === "logo" && el.image_url && (
                  <img 
                    src={el.image_url} 
                    alt="overlay"
                    className="w-full h-full object-contain pointer-events-none"
                    draggable={false}
                  />
                )}
                {el.type === "button" && (
                  <div 
                    className="w-full h-full flex items-center justify-center shadow-lg overflow-hidden"
                    style={{
                      backgroundColor: el.button_color,
                      color: el.button_text_color,
                      borderRadius: `${el.border_radius}px`,
                      fontSize: `clamp(6px, ${el.height * 0.5}%, 100px)`,
                      fontWeight: 'bold'
                    }}
                  >
                    {el.button_text}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* AI Reprompt */}
          <div className="p-3 border rounded-lg bg-purple-500/5 border-purple-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Wand2 className="h-4 w-4 text-purple-500" />
              <span className="text-sm font-medium">AI Regenerate</span>
            </div>
            <div className="flex gap-2">
              <Textarea
                spellCheck="true"
                value={regeneratePrompt}
                onChange={(e) => setRegeneratePrompt(e.target.value)}
                placeholder="Describe the image..."
                rows={2}
                className="flex-1 text-sm"
              />
              <Button 
                onClick={handleRegenerate} 
                disabled={regenerating || !regeneratePrompt.trim()}
                className="bg-purple-600 hover:bg-purple-700 self-end"
              >
                {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Static Add Forms */}
          <div className="space-y-3 border rounded-lg p-4 bg-secondary/30">
            <h4 className="text-sm font-medium mb-3">Add Overlays</h4>
            
            {/* TEXT Row */}
            <div className="flex items-center gap-2">
              <div className="w-16 flex items-center gap-1 text-sm font-medium text-muted-foreground">
                <Type className="h-4 w-4" />
                Text
              </div>
              <Input
                type="color"
                value={newTextColor}
                onChange={(e) => setNewTextColor(e.target.value)}
                className="w-10 h-8 p-0.5 cursor-pointer"
                title="Text Colour"
              />
              {brandColors.slice(0, 3).map(c => (
                <button
                  key={c.hex_code}
                  onClick={() => setNewTextColor(c.hex_code)}
                  className={`w-6 h-6 rounded border-2 ${newTextColor === c.hex_code ? 'border-blue-500' : 'border-transparent'}`}
                  style={{ backgroundColor: c.hex_code }}
                  title={c.name}
                />
              ))}
              <select
                value={newTextFont}
                onChange={(e) => setNewTextFont(e.target.value)}
                className="h-8 border rounded px-2 text-sm w-32"
              >
                {availableFonts.map(f => (
                  <option key={f.name} value={f.family}>{f.name}</option>
                ))}
              </select>
              <Input
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="Enter text"
                className="flex-1 h-8"
              />
              <Button size="sm" onClick={addTextElement} className="h-8 px-3">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* IMAGE Row */}
            <div className="flex items-center gap-2">
              <div className="w-16 flex items-center gap-1 text-sm font-medium text-muted-foreground">
                <Image className="h-4 w-4" />
                Image
              </div>
              <select
                value={newImageAsset}
                onChange={(e) => setNewImageAsset(e.target.value)}
                className="flex-1 h-8 border rounded px-2 text-sm"
              >
                <option value="">Select from your assets...</option>
                {companyAssets.map(asset => (
                  <option key={asset.id} value={asset.id}>{asset.name} ({asset.type})</option>
                ))}
              </select>
              {companyAssets.length === 0 && (
                <span className="text-xs text-muted-foreground">No assets uploaded</span>
              )}
              <Button size="sm" onClick={addImageElement} disabled={!newImageAsset} className="h-8 px-3">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* BUTTON Row */}
            <div className="flex items-center gap-2">
              <div className="w-16 flex items-center gap-1 text-sm font-medium text-muted-foreground">
                <Square className="h-4 w-4" />
                Button
              </div>
              <Input
                value={newButtonText}
                onChange={(e) => setNewButtonText(e.target.value)}
                placeholder="Button text"
                className="w-28 h-8"
              />
              <select
                value={newButtonShape}
                onChange={(e) => setNewButtonShape(e.target.value)}
                className="h-8 border rounded px-2 text-sm w-24"
              >
                <option value="rounded">Rounded</option>
                <option value="square">Square</option>
                <option value="pill">Pill</option>
              </select>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">BG:</span>
                <Input
                  type="color"
                  value={newButtonBg}
                  onChange={(e) => setNewButtonBg(e.target.value)}
                  className="w-8 h-8 p-0.5 cursor-pointer"
                />
              </div>
              {brandColors.slice(0, 2).map(c => (
                <button
                  key={c.hex_code}
                  onClick={() => setNewButtonBg(c.hex_code)}
                  className={`w-5 h-5 rounded border-2 ${newButtonBg === c.hex_code ? 'border-blue-500' : 'border-transparent'}`}
                  style={{ backgroundColor: c.hex_code }}
                  title={c.name}
                />
              ))}
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">Text:</span>
                <Input
                  type="color"
                  value={newButtonTextColor}
                  onChange={(e) => setNewButtonTextColor(e.target.value)}
                  className="w-8 h-8 p-0.5 cursor-pointer"
                />
              </div>
              <Button size="sm" onClick={addButtonElement} className="h-8 px-3">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Element count and clear all */}
          {overlayElements.length > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {overlayElements.length} overlay{overlayElements.length !== 1 ? 's' : ''} added
                <span className="ml-2 text-xs">(drag to move, corners to resize)</span>
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { setOverlayElements([]); setSelectedElement(null); }}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            </div>
          )}

          {/* Save Button */}
          <Button onClick={saveChanges} disabled={saving} className="w-full">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
